/** Patch helpers for the Value assumptions page — the draft is the effective
 *  assumption set being edited; the patch is the nested diff sent to
 *  PUT /value/assumptions (null = drop this set's override → inherited). */
export const PHASES = ["1", "2", "3", "4"];
export const MARKETS = ["US", "EU", "CA", "UK"];
export const SCALARS = [
    ["fixed_fees_usd", "Fixed fees / start-up (USD)", 500], ["win_rate", "Win rate (0–1)", 0.01], ["enrollment_achieved", "Enrollment achieved (0–1)", 0.01],
    ["max_patients_per_site", "Max patients / site", 1], ["min_listed_sites", "Min listed sites (registry basis)", 1],
    ["market_fit_outside", "Market fit outside your markets (0–1)", 0.05], ["default_area_factor", "Default area factor", 0.05],
];
export const P_LABEL = { registered: "Registered trial", announced_or_positive: "Announced / positive data", unverified_or_stale: "Unverified / stale", post_marketing: "Post-marketing", benchmark_default: "Benchmark default" };
export const WEIGHT_MIN = 0.25;
export const WEIGHT_MAX = 3;

export const deepClone = (x) => JSON.parse(JSON.stringify(x ?? null));

export function setIn(obj, path, value) {
    let d = obj;
    path.slice(0, -1).forEach((k) => { if (!d[k] || typeof d[k] !== "object") d[k] = {}; d = d[k]; });
    d[path[path.length - 1]] = value;
    return obj;
}

const num = (v) => (v === "" || v === null || v === undefined ? NaN : Number(v));
const same = (a, b) => (Number.isNaN(num(a)) && Number.isNaN(num(b))) || num(a) === num(b);

/** Diff two flat maps → `{key: value | null}` (null = removed). */
export function diffMap(orig = {}, cur = {}) {
    const out = {};
    new Set([...Object.keys(orig), ...Object.keys(cur)]).forEach((k) => {
        if (cur[k] === undefined || cur[k] === null || cur[k] === "") { if (orig[k] !== undefined) out[k] = null; return; }
        if (orig[k] === undefined || !same(orig[k], cur[k])) out[k] = typeof cur[k] === "boolean" ? cur[k] : num(cur[k]);
    });
    return out;
}

/** Nested patch = everything that differs between the loaded effective set and the draft. */
export function buildPatch(original, draft) {
    const p = {};
    const val = (v) => (v === null || v === undefined || v === "" ? null : num(v));  // null = reset to inherited
    PHASES.forEach((ph) => {
        ["low", "high"].forEach((k) => { if (!same(original.budget_per_patient?.[ph]?.[k], draft.budget_per_patient?.[ph]?.[k])) setIn(p, ["budget_per_patient", ph, k], val(draft.budget_per_patient[ph][k])); });
        if (!same(original.default_patients_per_site?.[ph], draft.default_patients_per_site?.[ph])) setIn(p, ["default_patients_per_site", ph], val(draft.default_patients_per_site[ph]));
    });
    SCALARS.forEach(([k]) => { if (!same(original[k], draft[k])) p[k] = val(draft[k]); });
    ["chance_trial_happens", "window_open"].forEach((grp) => {
        Object.keys(draft[grp] || {}).forEach((k) => { if (!same(original[grp]?.[k], draft[grp][k])) setIn(p, [grp, k], val(draft[grp][k])); });
    });
    if (JSON.stringify([...(draft.markets || [])].sort()) !== JSON.stringify([...(original.markets || [])].sort())) p.markets = draft.markets;
    if (!!draft.calibration?.auto !== !!original.calibration?.auto) setIn(p, ["calibration", "auto"], !!draft.calibration.auto);
    if (!same(original.calibration?.min_samples, draft.calibration?.min_samples)) setIn(p, ["calibration", "min_samples"], num(draft.calibration.min_samples));
    const maps = [
        [["area_factors"], original.area_factors, draft.area_factors],
        [["indication_factors"], original.indication_factors, draft.indication_factors],
        [["location", "index_overrides"], original.location?.index_overrides, draft.location?.index_overrides],
        [["country_index"], original.country_index, draft.country_index],
        [["weights", "areas"], original.weights?.areas, draft.weights?.areas],
        [["weights", "indications"], original.weights?.indications, draft.weights?.indications],
        [["locks"], original.locks, draft.locks],
    ];
    maps.forEach(([path, o, c]) => { const d = diffMap(o, c); if (Object.keys(d).length) setIn(p, path, d); });
    const shares = diffMap(original.location?.countries, draft.location?.countries);
    if (Object.keys(shares).length) {
        // shares replace the inherited map as a whole → always send the full current map (+ nulls for removed countries)
        const full = {};
        Object.entries(draft.location?.countries || {}).forEach(([c, s]) => { if (num(s) > 0) full[c] = num(s); });
        Object.keys(shares).forEach((c) => { if (shares[c] === null) full[c] = null; });
        setIn(p, ["location", "countries"], full);
    }
    return p;
}

/** Number of changed fields in a patch (the country-share map is sent whole, so only changed shares count). */
export function countPatch(p, original, draft) {
    let n = 0;
    const walk = (o, path = "") => Object.entries(o).forEach(([k, v]) => {
        if (path === "location." && k === "countries") { n += Object.keys(diffMap(original?.location?.countries, draft?.location?.countries)).length; return; }
        if (v && typeof v === "object" && !Array.isArray(v)) walk(v, `${path}${k}.`); else n += 1;
    });
    walk(p || {});
    return n;
}

export const fmtPct = (x) => `${Math.round(Number(x) * 100)} %`;
export const fmtIdx = (x) => Number(x).toFixed(2);

/** Calibration coverage row(s) for a lock key — `budget.3` · `area.Oncology` · `indication.<label>` · `fixed_fees`. */
export function coverageFor(cal, key) {
    if (!cal) return [];
    if (key === "fixed_fees") return cal.fixed_fees?.n ? [{ ...cal.fixed_fees, level: "fixed_fees", label: "start-up" }] : [];
    const [level, ...rest] = key.split(".");
    const k = rest.join(".");
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    return (cal.coverage || []).filter((r) => (level === "budget" ? r.level === "phase" && r.phase === k
        : level === "area" ? r.level === "area" && r.key === k
            : r.level === "indication" && (norm(r.label) === norm(k) || norm(r.key) === norm(k))));
}
